import {
  Button as FluentButton,
  ButtonProps as FluentButtonProps,
  Spinner,
} from '@fluentui/react-components';
import cn from 'classnames';
import React, { forwardRef } from 'react';
import './Button.scss';

export type ButtonProps = FluentButtonProps & {
  inPopover?: boolean;
  primaryTransparent?: boolean;
  /** Show a loading spinner and disable the button */
  loading?: boolean;
};

/**
 * Custom Button wrapper matching Northstar button styles.
 * - Adds padding, border-radius, and box-shadow to enabled buttons
 * - Disabled buttons have no shadow (matching Northstar behavior)
 * - primaryTransparent: transparent background with primary text color
 * - Supports `loading` prop to show a spinner and disable interaction
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ appearance, className, inPopover, primaryTransparent, loading, disabled, icon, children, ...props }, ref) => {
    const hasIcon = !!icon || loading;
    const isIconOnly = hasIcon && children == null && !loading;
    const isDisabled = disabled || loading;
    return (
      <FluentButton
        ref={ref}
        appearance={primaryTransparent ? 'transparent' : appearance}
        className={cn(
          'aix-button',
          primaryTransparent
            ? 'button--primary-transparent'
            : appearance && `button--${appearance}`,
          inPopover && 'button--in-popover',
          isIconOnly && 'button--icon-only',
          hasIcon && !isIconOnly && 'button--has-icon',
          isDisabled && 'button--disabled',
          className,
        )}
        disabled={isDisabled}
        icon={
          loading ? (
            <Spinner
              size="tiny"
              // Primary buttons have a brand-colored background and white text — the default
              // brand-colored spinner would be invisible on top. Use `inverted` (white) instead
              // so the spinner contrasts. Other appearances keep the brand-colored spinner.
              appearance={appearance === 'primary' ? 'inverted' : 'primary'}
            />
          ) : (
            icon
          )
        }
        {...props}
      >
        {children != null ? <span className="aix-button__content">{children}</span> : undefined}
      </FluentButton>
    );
  },
);

Button.displayName = 'Button';
